<script setup lang="ts">
import { ref, computed, useTemplateRef } from 'vue';
import { UiButton, UiText, UiCheckbox, useToaster } from '@shopexperts/shared';
import { useMatchExperts } from '../composables/useMatchExperts';
import ExpertSelector from './ExpertSelector.vue';

const toaster = useToaster();

interface AssignExpertsFormProps {
    leadId: number;
    leadTitle: string;
    currentMatchesCount: number;
    maxMatches: number;
}

const props = defineProps<AssignExpertsFormProps>();

const emit = defineEmits<{
    (e: 'success'): void;
    (e: 'cancel'): void;
}>();

// Form state
const formRef = useTemplateRef<HTMLFormElement>('formRef');
const selectorRef = useTemplateRef<InstanceType<typeof ExpertSelector>>('selectorRef');

// Selected expert IDs from the multi-select
const selectedExpertIds = ref<number[]>([]);

// Mark as resolved checkbox
const markAsResolved = ref(false);

const { mutateAsync: matchExperts, isPending: isAssigning, error } = useMatchExperts();

const availableSlots = computed(() => props.maxMatches - props.currentMatchesCount);

// Check for duplicates (shouldn't happen with multi-select but just in case)
const hasDuplicates = computed(() => {
    const ids = selectedExpertIds.value;
    return ids.length > 0 && new Set(ids).size !== ids.length;
});

// Validation message for UI feedback
const validationMessage = computed(() => {
    if (selectedExpertIds.value.length === 0) {
        return 'Please select at least one expert';
    }
    if (hasDuplicates.value) {
        return 'You cannot select the same expert multiple times';
    }
    if (selectedExpertIds.value.length > availableSlots.value) {
        return `You can only assign ${availableSlots.value} more expert(s) to this lead`;
    }
    return '';
});

// Form is valid when there's no validation message
const canSubmit = computed(() => {
    return selectedExpertIds.value.length > 0 && 
           !hasDuplicates.value && 
           selectedExpertIds.value.length <= availableSlots.value;
});

async function onFormSubmit() {
    // Validate before submitting
    if (!canSubmit.value) {
        if (validationMessage.value) {
            toaster.create({
                type: 'warning',
                title: 'Validation Error',
                description: validationMessage.value,
            });
        }
        return;
    }

    try {
        await matchExperts({
            leadId: props.leadId,
            expertIds: selectedExpertIds.value,
            markAsMatched: markAsResolved.value,
        });

        resetForm();
        emit('success');
    } catch (err) {
        console.error('[AssignExpertsForm] Error assigning experts:', err);
        toaster.create({
            type: 'error',
            title: 'Assignment Failed',
            description: 'Failed to assign experts. Please try again.',
        });
    }
}

function onCancel() {
    resetForm();
    emit('cancel');
}

function resetForm() {
    selectorRef.value?.reset();
    selectedExpertIds.value = [];
    markAsResolved.value = false;
    formRef.value?.reset();
}

function onExpertIdsChange(ids: number[]) {
    selectedExpertIds.value = ids;
}

defineExpose({
    resetForm,
});
</script>

<template>
    <form ref="formRef" @submit.prevent="onFormSubmit">
        <div class="flex flex-col gap-6">
            <!-- Lead Info -->
            <div class="p-4 bg-gray-50 rounded-md">
                <UiText as="p" variant="p-small" class="text-gray-600">
                    Assigning experts to:
                </UiText>
                <UiText as="p" variant="p" class="font-semibold mt-1">
                    {{ leadTitle }}
                </UiText>
                <UiText as="p" variant="p-small" class="text-gray-600 mt-1">
                    Currently matched: {{ currentMatchesCount }}/{{ maxMatches }} experts
                </UiText>
                <UiText as="p" variant="p-small" class="text-gray-600">
                    Available slots: {{ availableSlots }}
                </UiText>
            </div>

            <!-- Expert Multi-Select -->
            <ExpertSelector
                ref="selectorRef"
                label="Select Experts"
                placeholder="Search and select up to 3 experts..."
                :disabled="isAssigning"
                :max-selections="availableSlots"
                @update:selected-ids="onExpertIdsChange"
            />

            <!-- Validation Message -->
            <div v-if="validationMessage && selectedExpertIds.length > 0" class="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <UiText as="p" variant="p-small" class="text-yellow-800">
                    {{ validationMessage }}
                </UiText>
            </div>

            <!-- Error Message -->
            <div v-if="error" class="p-3 bg-red-50 border border-red-200 rounded-md">
                <UiText as="p" variant="p-small" class="text-red-800">
                    Failed to assign experts. Please try again.
                </UiText>
            </div>

            <!-- Mark as Resolved Checkbox -->
            <div class="flex items-start gap-3 p-3 bg-gray-50 rounded-md">
                <UiCheckbox
                    v-model="markAsResolved"
                    :disabled="isAssigning"
                />
                <div class="flex-1">
                    <UiText as="p" variant="p-small" class="font-medium mb-1">
                        Mark as Resolved
                    </UiText>
                    <UiText as="p" variant="caption-normal" class="text-gray-600">
                        Check this to mark the lead as matched/resolved after assigning experts. The lead will no longer appear in the pending list.
                    </UiText>
                </div>
            </div>

            <!-- Summary -->
            <div v-if="selectedExpertIds.length > 0 && canSubmit" class="p-3 bg-blue-50 border border-blue-200 rounded-md">
                <UiText as="p" variant="p-small" class="text-blue-800">
                    Ready to assign {{ selectedExpertIds.length }} expert(s){{ markAsResolved ? ' and mark as resolved' : '' }}
                </UiText>
            </div>

            <!-- Actions -->
            <div class="flex gap-3 pt-4 border-t">
                <UiButton
                    type="button"
                    variant="ghost"
                    label="Cancel"
                    :disabled="isAssigning"
                    @click="onCancel"
                />
                <UiButton
                    type="submit"
                    variant="primary"
                    :label="isAssigning ? 'Assigning...' : 'Assign Experts'"
                    :disabled="isAssigning || !canSubmit"
                />
            </div>
        </div>
    </form>
</template>
